import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import DealForgeLogo from "../components/DealForgeLogo";
import { toast } from "sonner";
import axios from "axios";
import { Mail, Lock, ArrowRight, Chrome } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hammer & Anvil Forge Icon
const ForgeIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M6 28H34V32C34 33.1 33.1 34 32 34H8C6.9 34 6 33.1 6 32V28Z" fill="currentColor"/>
    <path d="M4 24H36V28H4V24Z" fill="currentColor"/>
    <path d="M10 20H30V24H10V20Z" fill="currentColor"/>
    <path d="M20 6L24 10L20 14L16 10L20 6Z" fill="#D4AF37"/>
    <path d="M18 12L22 16V20H18V16L18 12Z" fill="#D4AF37"/>
    <circle cx="12" cy="18" r="1" fill="#D4AF37"/>
    <circle cx="28" cy="16" r="1" fill="#D4AF37"/>
  </svg>
);

export default function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      
      toast.success("Welcome back!");
      onLogin(response.data);
      
      // Redirect to onboarding if user hasn't selected a primary portal yet
      if (!response.data.primary_portal) {
        navigate("/onboarding");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-black flex">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 py-12 lg:px-16">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <Link to="/" className="inline-flex items-center mb-12">
            <DealForgeLogo size="xl" showTM={true} />
          </Link>

          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-white uppercase mb-2">
              Welcome Back
            </h1>
            <p className="text-white/70">
              Sign in to access your account
            </p>
          </div>

          {/* Beta Notice */}
          <div className="bg-purple-500/10 border border-purple-500/20 p-4 rounded-2xl mb-6">
            <p className="text-purple-300 text-sm">
              <strong>Founder Beta</strong> — Access is currently limited to approved beta members.
            </p>
            <Link to="/" className="text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] hover:text-purple-300 text-sm mt-2 inline-block">
              ← Join the waitlist
            </Link>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70 text-sm font-medium">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 pl-11 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white placeholder:text-white/70 rounded-sm focus:border-gold focus:ring-gold"
                  placeholder="you@example.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70 text-sm font-medium">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pl-11 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white placeholder:text-white/70 rounded-sm focus:border-gold focus:ring-gold"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 gold-gradient-btn rounded-none font-bold uppercase tracking-wide"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-white/50">
            Don't have access?{" "}
            <Link to="/" className="text-gold hover:text-gold-light font-medium">
              Join the waitlist
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Branding */}
      <div className="hidden lg:flex w-1/2 bg-[#0A0A0A]/80 backdrop-blur-sm relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("https://images.unsplash.com/photo-1761437855740-c894da924d79?crop=entropy&cs=srgb&fm=jpg&q=85")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }} />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 to-transparent" />
        
        <div className="relative z-10 flex flex-col justify-center p-16">
          <div className="max-w-md">
            <img src="/logo.png" alt="DealForge" className="h-28 w-auto mb-8" />
            <h2 className="font-heading text-4xl font-black uppercase text-white mb-4">
              ANALYZE.<br />PRIORITIZE.<br />CLOSE.
            </h2>
            <p className="text-white/70 text-lg">
              The operating system built for real estate operators who execute.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
